import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import "./ProductPage.css";
import axios from "axios";
import { startLoading, stopLoading } from "../../Redux Store/Slice";
import { useDispatch } from "react-redux";
import ProductSkeleton from "./ProductSkeleton";
import { addToCart } from "../../utils/addToCart";

const PLACEHOLDER_IMAGE =
  "https://upload.wikimedia.org/wikipedia/commons/thumb/6/65/No-Image-Placeholder.svg/1665px-I No-Image-Placeholder.svg.png";
const IMAGE_BASE_URL = "https://backend-wellshape.vercel.app";

// Dummy Review Data
const DUMMY_REVIEWS = [
  {
    id: 1,
    rating: 5.0,
    user: "Sarah K.",
    date: "Oct 20, 2025",
    comment:
      "Absolutely love these! The fit is perfect, and the material feels premium. They hold up great during a heavy workout.",
  },
  {
    id: 2,
    rating: 4.0,
    user: "Mark T.",
    date: "Oct 15, 2025",
    comment:
      "Great product for the price. Only took off one star because the color was slightly different than the photo, but the quality is excellent.",
  },
  {
    id: 3,
    rating: 4.5,
    user: "Emily R.",
    date: "Oct 12, 2025",
    comment:
      "Very comfortable and breathable. I appreciate the high waist; it stays put. Will definitely buy another pair!",
  },
  {
    id: 4,
    rating: 5.0,
    user: "David L.",
    date: "Sep 28, 2025",
    comment:
      "My go-to gear now. Fast shipping and the sizing was spot on using the chart. Five stars!",
  },
  {
    id: 5,
    rating: 3.5,
    user: "Jessica M.",
    date: "Sep 25, 2025",
    comment:
      "Decent quality, but a little too long for my liking (I’m 5’2”). Still good for exercising.",
  },
];

const ProductPage = () => {
  const { slug } = useParams();
  const [selectedSize, setSelectedSize] = useState("");
  const [openAccordion, setOpenAccordion] = useState("description");
  const [selectedImage, setSelectedImage] = useState(0);
  const [product, setProduct] = useState(null);
  const [productImages, setProductImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showReviews, setShowReviews] = useState(false); // New state for reviews section
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // ... (handleCheckout function remains the same)
  const handleCheckout = async () => {
    try {
      const cartId = localStorage.getItem("cartId");
      if (!cartId) return;

      let user = JSON.parse(localStorage.getItem("user"));

      const { data } = await axios.post(
        `${API_URL}/order/checkout/create-session`,
        {
          cartId,
          userId: user ? user.id : null,
        },
      );

      if (data.url) {
        window.location.href = data.url; // redirect user straight to Stripe
      } else {
        console.error("Checkout session URL not found in response.", data);
        alert("Failed to create checkout session.");
      }
    } catch (err) {
      console.error("Checkout error:", err);
      alert("Something went wrong. Please try again.");
    }
  };

  // ... (useEffect for fetching product data remains the same)
  // Fetch product data
  useEffect(() => {
    setLoading(true);
    fetch(`${IMAGE_BASE_URL}/products/${slug}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success && data.product) {
          const p = data.product;
          setProduct(p);
          console.log(p);
          // Prepare image URLs
          const rawImages = [p.image_url, ...(p.extra_images || [])];
          const validImages = rawImages.map((img) => {
            if (
              !img ||
              img.trim() === "" ||
              img.includes("dummy") ||
              img.includes("/images/products/")
            ) {
              // Check if it's a relative path like "/images/..."
              if (img && img.startsWith("/images/")) {
                return "https://upload.wikimedia.org/wikipedia/commons/thumb/6/65/No-Image-Placeholder.svg/1665px-No-Image-Placeholder.svg.png";
              }
              return PLACEHOLDER_IMAGE;
            }
            // If full URL already
            return img.startsWith("http") ? img : `${IMAGE_BASE_URL}${img}`;
          });

          setProductImages(validImages);

          // Select default size
          const inStockSize = p.variants.find((v) => v.stock_quantity > 0);
          setSelectedSize(inStockSize ? inStockSize.size : "");

          setError(null);
        } else if (data.product === null) {
          setError("Product not found.");
        } else {
          setError(data.message || "Unknown error.");
        }
        setLoading(false);
      })
      .catch(() => {
        setError("Failed to load product.");
        setLoading(false);
      });
  }, [slug]);

  const handleAccordionToggle = (section) => {
    setOpenAccordion(openAccordion === section ? null : section);
  };

  const handleSizeSelect = (size) => {
    setSelectedSize(size);
  };

  const handleImageSelect = (index) => {
    setSelectedImage(index);
  };
  const [showSizeChart, setShowSizeChart] = useState(false);
  // New function to toggle the reviews section
  const handleReviewToggle = () => {
    setShowReviews((prev) => !prev);
  };

  // Helper component to render stars
  const StarRating = ({ rating }) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    return (
      <div className="star-rating">
        {Array(fullStars)
          .fill("★")
          .map((s, i) => (
            <span key={`full-${i}`} className="star filled">
              {s}
            </span>
          ))}
        {hasHalfStar && <span className="star half">½</span>}
        {Array(emptyStars)
          .fill("★")
          .map((s, i) => (
            <span key={`empty-${i}`} className="star empty">
              {s}
            </span>
          ))}
      </div>
    );
  };
  // let dispatch = useDispatch()
  // useEffect(() => {
  //   if (loading) {
  //     dispatch(startLoading());
  //   } else {
  //     setTimeout(() => {
  //       dispatch(stopLoading());
  //     }, 1500)
  //   }
  // }, [loading]);

  if (loading) {
    return <ProductSkeleton />;
  }

  if (error) {
    return (
      <div className="product-page">
        <div className="product-container">
          <p className="error-message">{error}</p>
        </div>
      </div>
    );
  }

  const variant =
    product.variants?.find((v) => v.size === selectedSize) ||
    product.variants?.[0] ||
    null;
  const price =
    variant && variant.price_override
      ? Number(variant.price_override)
      : Number(product.base_price);
  const sizes = product.variants.map((v) => ({
    size: v.size,
    inStock: v.stock_quantity > 0,
  }));

  return (
    <div className="product-page">
      <div className="product-container">
        {/* Product Images */}
        <div className="product-images">
          <div className="main-image">
            <img
              src={productImages[selectedImage] || PLACEHOLDER_IMAGE}
              alt={product.name}
            />
            <button className="model-size-btn">+ Model Size</button>
          </div>
          <div className="thumbnail-images">
            {productImages.map((image, index) => (
              <div
                key={index}
                className={`thumbnail ${selectedImage === index ? "selected" : ""}`}
                onClick={() => handleImageSelect(index)}
              >
                <img
                  src={image || PLACEHOLDER_IMAGE}
                  alt={`${product.name} ${index + 1}`}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Product Details */}
        <div className="product-details">
          <div className="product-header">
            <h1 className="product-title">{product.name}</h1>
            <button className="wishlist-btn">♥</button>
          </div>

          <p className="product-color">{product.color}</p>

          <div className="product-pricing">
            <div className="price-container">
              <span className="current-price">£{price.toFixed(2)}</span>
              {variant && variant.price_override && (
                <span className="original-price">
                  £{Number(product.base_price).toFixed(2)}
                </span>
              )}
            </div>
          </div>

          {/* Reviews Toggle Block - ADD onClick Handler */}
          <div className="product-rating" onClick={handleReviewToggle}>
            <span className="rating">4.0</span>
            <span className="star">★</span>
          </div>

          <div className="size-selection">
            <div className="size-header">
              <h3>Size</h3>
              <button
                className="size-chart-link"
                onClick={(e) => {
                  e.preventDefault();
                  setShowSizeChart(true);
                }}
              >
                Size chart
              </button>
            </div>
            <div className="size-options">
              {sizes.map((s) => (
                <button
                  key={s.size}
                  className={`size-btn ${selectedSize === s.size ? "selected" : ""} ${!s.inStock ? "out-of-stock" : ""}`}
                  onClick={() => handleSizeSelect(s.size)}
                  disabled={!s.inStock}
                >
                  {s.size}
                  {selectedSize === s.size && s.inStock && (
                    <span className="in-stock">In stock</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="action-buttons">
            <button
              className="add-to-cart-btn"
              onClick={() => {
                if (!selectedSize) {
                  alert("Please select a size");
                  return;
                }
                const selectedVariant = product.variants.find(
                  (v) => v.size === selectedSize,
                );
                if (selectedVariant) {
                  console.log(
                    "Adding to cart (from product page) :",
                    product.id,
                    1,
                    selectedVariant.variant_id,
                  );
                  addToCart(product.product_id, 1, selectedVariant.variant_id);
                }
              }}
              disabled={!selectedSize}
            >
              ADD TO CART
            </button>

            <button
              className="shop-pay-btn"
              onClick={async () => {
                if (!selectedSize) {
                  alert("Please select a size");
                  return;
                }
                const selectedVariant = product.variants.find(
                  (v) => v.size === selectedSize,
                );
                if (selectedVariant) {
                  await addToCart(
                    product.product_id,
                    1,
                    selectedVariant.variant_id,
                  );
                  await handleCheckout();
                }
              }}
              disabled={!selectedSize}
            >
              🛒 ADD & CHECKOUT
            </button>
          </div>

          <div className="payment-options">
            <a href="#" className="more-payment-link">
              More payment options
            </a>
            <div className="klarna-option">
              <span>Buy Now Pay Later with Klarna</span>
              <div className="klarna-logo">Klarna</div>
            </div>
          </div>

          <div className="shipping-returns">
            <div className="shipping-info">
              <span className="icon">🚚</span>
              <span>Free UK Shipping Over £45</span>
            </div>
            <div className="returns-info">
              <span className="icon">🔄</span>
              <span>Free UK 30 Day Returns</span>
            </div>
          </div>

          {/* Accordion Sections (Code remains the same) */}
          <div className="accordion-sections">
            <div
              className={`accordion-section ${openAccordion === "description" ? "open" : ""}`}
            >
              <div
                className="accordion-header"
                onClick={() => handleAccordionToggle("description")}
              >
                <h3>DESCRIPTION</h3>
                <span className="accordion-arrow">
                  {openAccordion === "description" ? "▲" : "▼"}
                </span>
              </div>
              <div className="accordion-content">
                <p>{product.description}</p>
              </div>
            </div>

            <div
              className={`accordion-section ${openAccordion === "sizeAndFit" ? "open" : ""}`}
            >
              <div
                className="accordion-header"
                onClick={() => handleAccordionToggle("sizeAndFit")}
              >
                <h3>SIZE & FIT</h3>
                <span className="accordion-arrow">
                  {openAccordion === "sizeAndFit" ? "▲" : "▼"}
                </span>
              </div>
              <div className="accordion-content">
                <p>Size and fit information coming soon.</p>
              </div>
            </div>

            <div
              className={`accordion-section ${openAccordion === "materialAndCare" ? "open" : ""}`}
            >
              <div
                className="accordion-header"
                onClick={() => handleAccordionToggle("materialAndCare")}
              >
                <h3>MATERIAL & CARE</h3>
                <span className="accordion-arrow">
                  {openAccordion === "materialAndCare" ? "▲" : "▼"}
                </span>
              </div>
              <div className="accordion-content">
                <p>Material and care details coming soon.</p>
              </div>
            </div>

            <div
              className={`accordion-section ${openAccordion === "deliveryAndReturns" ? "open" : ""}`}
            >
              <div
                className="accordion-header"
                onClick={() => handleAccordionToggle("deliveryAndReturns")}
              >
                <h3>DELIVERY & RETURNS</h3>
                <span className="accordion-arrow">
                  {openAccordion === "deliveryAndReturns" ? "▲" : "▼"}
                </span>
              </div>
              <div className="accordion-content">
                <p>
                  Free UK shipping on orders over £45. Free 30-day returns.
                  Standard delivery takes 3-5 business days.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Size Chart Modal */}
      {showSizeChart && (
        <div
          className="size-chart-modal-overlay"
          onClick={() => setShowSizeChart(false)}
        >
          <div
            className="size-chart-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="modal-close-btn"
              onClick={() => setShowSizeChart(false)}
            >
              ×
            </button>

            <h2>WOMEN'S TOPS</h2>
            <p className="size-chart-description">
              When it comes to performance, fit is key. That's why we've worked
              hard to develop a sizing chart to help you find the perfect fit
              for your AYBL, so you can perform at your best.
            </p>

            <div className="size-chart-toggle">
              <span className="size-chart-label">SIZE CHART</span>
              <div className="unit-toggle">
                <span>cm</span>
                <label className="toggle-switch">
                  <input type="checkbox" />
                  <span className="toggle-slider"></span>
                </label>
                <span>in</span>
              </div>
            </div>

            <div className="size-chart-table">
              <table>
                <thead>
                  <tr>
                    <th>Mate</th>
                    <th>Bust</th>
                    <th>Waist</th>
                    <th>Hip</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>XXS</td>
                    <td>76.5 - 79.5</td>
                    <td>58.5 - 61.5</td>
                    <td>85.5 - 88.5</td>
                  </tr>
                  <tr>
                    <td>XS</td>
                    <td>79.5 - 85.5</td>
                    <td>61.5 - 67.5</td>
                    <td>88.5 - 94.5</td>
                  </tr>
                  <tr>
                    <td>S</td>
                    <td>85.5 - 91.5</td>
                    <td>67.5 - 73.5</td>
                    <td>94.5 - 100.5</td>
                  </tr>
                  <tr>
                    <td>M</td>
                    <td>91.5 - 97.5</td>
                    <td>73.5 - 79.5</td>
                    <td>100.5 - 106.5</td>
                  </tr>
                  <tr>
                    <td>L</td>
                    <td>97.5 - 103.5</td>
                    <td>79.5 - 85.5</td>
                    <td>106.5 - 112.5</td>
                  </tr>
                  <tr>
                    <td>XL</td>
                    <td>103.5 - 109.5</td>
                    <td>85.5 - 91.5</td>
                    <td>112.5 - 118.5</td>
                  </tr>
                  <tr>
                    <td>XXL</td>
                    <td>109.5 - 115.5</td>
                    <td>91.5 - 97.5</td>
                    <td>118.5 - 124.5</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3 className="carrying-size-title">CARRYING SIZE</h3>

            <div className="size-chart-table">
              <table>
                <thead>
                  <tr>
                    <th>Mate</th>
                    <th>UK</th>
                    <th>US</th>
                    <th>EU</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>XXS</td>
                    <td>UK 2 - 4</td>
                    <td>US 0 - 2</td>
                    <td>EU 30 - 32</td>
                  </tr>
                  <tr>
                    <td>XS</td>
                    <td>UK 6 - 8</td>
                    <td>US 2 - 4</td>
                    <td>EU 34 - 36</td>
                  </tr>
                  <tr>
                    <td>S</td>
                    <td>UK 8 - 10</td>
                    <td>US 4 - 6</td>
                    <td>EU 36 - 38</td>
                  </tr>
                  <tr>
                    <td>M</td>
                    <td>UK 10 - 12</td>
                    <td>US 6 - 8</td>
                    <td>EU 38 - 40</td>
                  </tr>
                  <tr>
                    <td>L</td>
                    <td>UK 12 - 14</td>
                    <td>US 8 - 10</td>
                    <td>EU 40 - 42</td>
                  </tr>
                  <tr>
                    <td>XL</td>
                    <td>UK 14 - 16</td>
                    <td>US 10 - 12</td>
                    <td>EU 42 - 44</td>
                  </tr>
                  <tr>
                    <td>XXL</td>
                    <td>UK 18 - 20</td>
                    <td>US 14 - 16</td>
                    <td>EU 46 - 48</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      {/* Reviews Section - Renders outside the product-container, conditional based on state */}
      {showReviews && (
        <div className="reviews-section">
          <div className="reviews-header-container">
            {" "}
            {/* New wrapper for header */}
            <h2 className="reviews-title">
              Customer Reviews ({DUMMY_REVIEWS.length})
            </h2>
            <button className="write-review-btn">Write a Review</button>
          </div>

          <div className="review-list">
            {DUMMY_REVIEWS.map((review) => (
              <div key={review.id} className="review-card">
                <div className="review-header">
                  <StarRating rating={review.rating} />
                  <span className="review-rating-text">
                    {review.rating.toFixed(1)} / 5.0
                  </span>
                </div>

                <p className="review-comment">{review.comment}</p>

                <div className="review-user-info-container">
                  <span className="review-user-name">{review.user}</span>
                  <span className="review-date">{review.date}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductPage;
